import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { boostService } from "../../features/boost/service";
import type { Campaign, CampaignAnalytics, CampaignInsights } from "../../features/boost/types";
import { parseApiError } from "../../lib/http/apiErrors";

const FUNNEL_COLORS: Record<string, string> = {
  views:     "#9ca3af",
  likes:     "#ec4899",
  saves:     "#f59e0b",
  enquiries: "#f97316",
  accepted:  "#22c55e",
  contacted: "#3b82f6",
};

function fmtCredits(n: number) { return `${n.toLocaleString("en-IN", { maximumFractionDigits: 1 })} cr`; }

function pct(a: number, b: number) {
  if (!b) return 0;
  return Math.min(100, Math.round((a / b) * 100));
}

function ctrStr(clicks: number, impressions: number) {
  if (!impressions) return "—";
  return `${((clicks / impressions) * 100).toFixed(1)}%`;
}

function fmtDate(d: string | Date) {
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

interface FunnelRow {
  label: string;
  value: number;
  total: number;
  color: string;
}

function FunnelBar({ row }: { row: FunnelRow }) {
  const p = pct(row.value, row.total);
  const convPct = row.total > 0 ? Math.round((row.value / row.total) * 100) : null;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-28 text-sm text-gray-700 shrink-0">{row.label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
        <div
          className="h-2 rounded-full transition-all"
          style={{ width: `${p}%`, backgroundColor: row.color }}
        />
      </div>
      <span className="w-6 text-sm font-semibold text-gray-900 text-right shrink-0">{row.value}</span>
      {convPct !== null && (
        <span className="w-10 text-xs text-gray-400 text-right shrink-0">
          {convPct > 0 ? `${convPct}%` : ""}
        </span>
      )}
    </div>
  );
}

function AudienceBar({ label, pct: p }: { label: string; pct: number }) {
  return (
    <div className="mb-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-semibold text-gray-900">{p.toFixed(1)}%</span>
      </div>
      <div className="bg-gray-100 rounded-full h-2 overflow-hidden">
        <div className="h-2 rounded-full bg-gray-900 transition-all" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

export default function BoostAnalyticsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [insights, setInsights] = useState<CampaignInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      boostService.getCampaign(id),
      boostService.getAnalytics(id),
      boostService.getInsights(id).catch(() => null), // insights optional — don't block page
    ])
      .then(([c, a, i]) => { setCampaign(c); setAnalytics(a); setInsights(i); })
      .catch(e => setError(parseApiError(e, "Failed to load analytics").message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-7 h-7 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !campaign || !analytics) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50 px-6">
        <p className="text-red-500 text-sm">{error || "Campaign not found"}</p>
        <button onClick={() => navigate("/boost/campaigns")} className="text-blue-600 text-sm underline">
          Back to campaigns
        </button>
      </div>
    );
  }

  const spentPct = pct(campaign.spent_credits ?? 0, campaign.total_budget_credits ?? 1);
  const impressions = analytics.total_impressions;
  const clicks = analytics.total_clicks;
  const searches = analytics.total_search_appearances;

  // Real boosted-deal funnel (within campaign window). Scaled to the top stage.
  const funnel = insights?.funnel ?? [];
  const funnelTop = funnel.length ? Math.max(funnel[0].count, 1) : 1;

  const audienceReal = insights?.audience ?? [];
  const regionsReal = insights?.regions ?? [];

  const surfaceData = insights?.surface_breakdown ?? [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/boost/campaigns")} className="p-1 -ml-1 text-gray-600 hover:text-gray-900">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="font-bold text-base text-gray-900">Boost Analytics</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">

        {/* Deal title + status */}
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center mb-3 shadow">
            <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
            </svg>
          </div>
          {campaign.deal_title && (
            <p className="text-sm font-semibold text-gray-800 line-clamp-2">{campaign.deal_title}</p>
          )}
          <p className="text-xs text-gray-400 mt-0.5 capitalize">{campaign.status.toLowerCase()}</p>
        </div>

        {/* Top stats */}
        <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4">
          <div className="grid grid-cols-4 divide-x divide-gray-100">
            {[
              { label: "Impressions", value: impressions.toLocaleString() },
              { label: "Clicks",      value: clicks.toLocaleString() },
              { label: "CTR",         value: ctrStr(clicks, impressions) },
              { label: "Spent",       value: `${fmtCredits(campaign.spent_credits ?? 0)} / ${fmtCredits(campaign.total_budget_credits ?? 0)}` },
            ].map(s => (
              <div key={s.label} className="flex flex-col items-center gap-0.5 px-1">
                <span className="text-[10px] text-gray-400 text-center">{s.label}</span>
                <span className="text-sm font-bold text-gray-900 text-center leading-tight">{s.value}</span>
              </div>
            ))}
          </div>

          {/* Auction economics — you pay per view, at a price set by competition */}
          {impressions > 0 && (
            <div className="mt-4 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-emerald-700 uppercase tracking-wide font-semibold">
                    Avg paid per view
                  </p>
                  <p className="text-sm font-bold text-emerald-900">
                    {fmtCredits(analytics.avg_price_credits ?? 0)}
                    <span className="text-[11px] font-normal text-emerald-700">
                      {" "}/ {fmtCredits(campaign.bid_credits ?? 0)} max bid
                    </span>
                  </p>
                </div>
                {(analytics.avg_price_paise ?? 0) < (campaign.bid_paise ?? 0) && (
                  <span className="text-[10px] bg-emerald-600 text-white px-2 py-1 rounded-full font-semibold">
                    Paying below your bid
                  </span>
                )}
              </div>
              <p className="text-[10px] text-emerald-700 mt-1 leading-snug">
                You're charged only when your deal is shown, and only enough to outrank
                the next advertiser — so you usually pay less than your max bid.
              </p>
            </div>
          )}

          {/* Budget progress */}
          <div className="mt-4">
            <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 rounded-full bg-blue-500 transition-all"
                style={{ width: `${spentPct}%` }}
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-gray-400">{spentPct}% spent</span>
              <span className="text-[10px] text-gray-400">{fmtDate(campaign.end_date)}</span>
            </div>
          </div>
        </div>

        {/* Conversion Funnel (real, within campaign window) */}
        <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Conversion funnel (campaign period)
          </p>
          {funnel.length > 0 ? (
            funnel.map(stage => (
              <FunnelBar
                key={stage.key}
                row={{
                  label: stage.label,
                  value: stage.count,
                  total: funnelTop,
                  color: FUNNEL_COLORS[stage.key] ?? "#6b7280",
                }}
              />
            ))
          ) : (
            <p className="text-sm text-gray-400 py-3 text-center">No engagement recorded yet.</p>
          )}

          {/* Reach + spend summary */}
          <div className="mt-3 pt-3 border-t border-gray-50 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-gray-400">Search hits</p>
              <p className="text-base font-bold text-gray-900">{searches.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Total spend</p>
              <p className="text-base font-bold text-gray-900">{fmtCredits(analytics.total_spend_credits ?? 0)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Boost score</p>
              <p className="text-base font-bold text-gray-900">{campaign.boost_score.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Surfaces */}
        <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Active surfaces</p>
          <p className="text-sm font-semibold text-gray-900 mb-3">Where your deal is shown</p>
          {surfaceData.length === 0 && <p className="text-sm text-gray-400 py-2">No impression data yet.</p>}
          {surfaceData.map(s =>
            s.surface === "notify"
              ? <div key={s.label} className="mb-3 flex justify-between text-sm"><span className="text-gray-700">{s.label}</span><span className="font-semibold text-gray-500">Sent</span></div>
              : <AudienceBar key={s.label} label={s.label} pct={s.pct} />
          )}
        </div>

        {/* Audience — real, who viewed the boosted deal */}
        <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Audience</p>
          <p className="text-sm font-semibold text-gray-900 mb-3">Who viewed your listing</p>
          {audienceReal.length > 0 ? (
            audienceReal.map(a => <AudienceBar key={a.label} label={a.label} pct={a.pct} />)
          ) : (
            <p className="text-sm text-gray-400 py-2">No viewer data yet.</p>
          )}

          {regionsReal.length > 0 && (
            <>
              <p className="text-sm font-semibold text-gray-900 mt-4 mb-3">Audience by region</p>
              {regionsReal.map(r => <AudienceBar key={r.country} label={r.country} pct={r.pct} />)}
            </>
          )}
        </div>

        {/* Daily breakdown */}
        {analytics.days.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 px-4 py-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Daily breakdown</p>
            <div className="space-y-2">
              {analytics.days.map(d => (
                <div key={d.date} className="flex items-center justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                  <span className="text-gray-500 w-20 shrink-0">{fmtDate(d.date)}</span>
                  <span className="text-gray-700">{d.impressions} imp</span>
                  <span className="text-pink-500">{d.clicks} clk</span>
                  <span className="text-orange-500">{d.search_appearances} srch</span>
                  <span className="text-gray-900 font-medium">{fmtCredits(d.spend_credits ?? 0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="h-6" />
      </div>
    </div>
  );
}
